import api from '../../services/api';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunk for getting all examinations
export const getExaminationsList = createAsyncThunk(
  'examinations/list',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: '/exam/',
        method: 'GET',
      });
      return response?.data;
    } catch (error) {
      const errorMessage = error.response?.data || error.message;
      return rejectWithValue(errorMessage || 'Failed to fetch examinations');
    }
  },
);

// Async thunk for creating a new examination
export const createExamination = createAsyncThunk(
  'examinations/create',
  async (examinationData, { rejectWithValue }) => {
    try {
      const payload = {
        examType: examinationData.examType,
      };
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: '/exam/',
        method: 'POST',
        data: payload,
      });
      return response?.data;
    } catch (error) {
      const errorMessage = error.response?.data || error.message;
      return rejectWithValue(errorMessage || 'Failed to create examination');
    }
  },
);

// Async thunk for updating an examination
export const updateExamination = createAsyncThunk(
  'examinations/update',
  async (examTypeData, { rejectWithValue }) => {
    try {
      const payload = examTypeData;
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: `/exam/`,
        method: 'PUT',
        data: payload,
      });
      return { id: payload.id, data: response?.data };
    } catch (error) {
      const errorMessage = error.response?.data || error.message;
      return rejectWithValue(errorMessage || 'Failed to update examination');
    }
  },
);

// Async thunk for deleting an examination
export const deleteExamination = createAsyncThunk(
  'examinations/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        url: `/exam/${id}`,
        method: 'DELETE',
      });
      return { id, status: response?.status };
    } catch (error) {
      const errorMessage = error.response?.data || error.message;
      return rejectWithValue(errorMessage || 'Failed to delete examination');
    }
  },
);

const examinationSlice = createSlice({
  name: 'examinations',
  initialState: {
    examinationsList: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
  },
  reducers: {
    // Clear error message
    clearExaminationError: (state) => {
      state.error = null;
    },
    // Reset status to idle
    resetExaminationStatus: (state) => {
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get examinations list cases
      .addCase(getExaminationsList.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getExaminationsList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.examinationsList = action.payload || [];
        state.error = null;
      })
      .addCase(getExaminationsList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Create examination cases
      .addCase(createExamination.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createExamination.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Add the new examination to the list if it's provided
        if (action.payload) {
          state.examinationsList.push(action.payload);
        }
        state.error = null;
      })
      .addCase(createExamination.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Update examination cases
      .addCase(updateExamination.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateExamination.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Update the examination in the list
        const { id, data } = action.payload;
        const existingExaminationIndex = state.examinationsList.findIndex(
          (examination) => examination.id === id,
        );
        if (existingExaminationIndex !== -1 && data) {
          state.examinationsList[existingExaminationIndex] = data;
        }
        state.error = null;
      })
      .addCase(updateExamination.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Delete examination cases
      .addCase(deleteExamination.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteExamination.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Remove the examination from the list
        const { id } = action.payload;
        state.examinationsList = state.examinationsList.filter(
          (examination) => examination.id !== id,
        );
        state.error = null;
      })
      .addCase(deleteExamination.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

// Export reducer
export default examinationSlice.reducer;
